import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { EaPromoDialog } from "./EaPromoDialog";

export function eaVerifyUrl(companyId, year) {
  const q = new URLSearchParams({ company: companyId, ...(year ? { year: String(year) } : {}) });
  return `/energy-analytics/overview?${q.toString()}`;
}

export function EaVerifyLink({ companyId, year, testid, compact }) {
  if (!companyId) return null;
  return (
    <a href={eaVerifyUrl(companyId, year)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={testid}
      className={`inline-flex items-center gap-1 font-semibold text-purple-700 hover:underline ${compact ? "text-[11px]" : "text-xs"}`}>
      Verifica in Energy Analytics <ArrowRight size={12} />
    </a>
  );
}

const BG = "radial-gradient(ellipse at 92% 0%, #3A1C9E 0%, transparent 45%), radial-gradient(ellipse at 0% 100%, #1E3FBF 0%, transparent 40%), #0A0F3D";

function Decor() {
  return (
    <>
      <div className="absolute right-0 bottom-0 w-1/2 h-full opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(#8FB3FF 1px, transparent 1px)", backgroundSize: "12px 12px", maskImage: "linear-gradient(to left, black, transparent 80%)", WebkitMaskImage: "linear-gradient(to left, black, transparent 80%)" }} />
      <svg className="absolute bottom-0 left-0 w-full h-full pointer-events-none" viewBox="0 0 1000 120" preserveAspectRatio="none" aria-hidden="true">
        <defs><linearGradient id="eaw" x1="0" x2="1"><stop offset="0%" stopColor="#3285FD" stopOpacity="0" /><stop offset="60%" stopColor="#5B7CFF" stopOpacity="0.8" /><stop offset="100%" stopColor="#B26BFF" stopOpacity="0.9" /></linearGradient></defs>
        <path d="M0 118 C 300 122, 550 110, 720 80 S 920 30, 1000 20" fill="none" stroke="url(#eaw)" strokeWidth="1.6" style={{ filter: "drop-shadow(0 0 6px #6D8BFF)" }} />
      </svg>
    </>
  );
}

function Body({ isStaff, last, enabled }) {
  return (
    <div className="flex items-center gap-5 flex-wrap relative">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-white" style={{ background: "linear-gradient(135deg, #B26BFF, #7B2CFF)", boxShadow: "0 0 0 1px #B26BFF80, 0 10px 30px #7B2CFF66" }}>
        <BarChart3 size={26} />
      </div>
      <div className="flex-1 min-w-[240px]">
        <div className="font-display text-2xl font-bold tracking-tight leading-tight" data-testid="dashboard-ea-title">Energy Analytics</div>
        <div className="font-display text-lg font-semibold text-white/95 leading-snug" data-testid="dashboard-ea-subtitle">Il tuo portafoglio energetico sotto controllo</div>
        <div className="text-sm text-white/70 mt-1" data-testid="dashboard-ea-text">Consumi, costi, forecast e analisi per prendere decisioni consapevoli.</div>
        {isStaff && last && (
          <div className="text-xs text-white/80 mt-2" data-testid="dashboard-ea-last-import">
            Ultimo cliente importato: <b>{last.company_name}</b> · {last.point} · {last.year}
            {!last.enabled && <span className="ml-2 text-amber-300">(modulo non attivo per il cliente)</span>}
          </div>
        )}
      </div>
      <span className="inline-flex items-center gap-2 bg-white text-[#0A0F3D] font-semibold text-sm rounded-xl px-5 py-2.5 shadow-lg shadow-purple-900/30 transition-transform duration-200 group-hover:translate-x-1" data-testid="dashboard-ea-open">
        {!enabled ? <><Sparkles size={16} className="text-purple-600" /> Scopri il modulo</> : <>{isStaff && last ? "Verifica ultimo cliente" : "Accedi all'analisi"} <ArrowRight size={16} /></>}
      </span>
    </div>
  );
}

export function EaDashboardCard({ isStaff, enabled }) {
  const [last, setLast] = useState(null);
  const [promo, setPromo] = useState(false);
  useEffect(() => { if (isStaff) api.get("/ea/last-import").then(({ data }) => setLast(data)).catch(() => setLast(null)); }, [isStaff]);
  const cls = "block w-full text-left rounded-[18px] text-white p-6 relative overflow-hidden group border border-purple-400/30 shadow-[0_0_30px_-8px_rgba(123,44,255,0.45)]";
  if (!enabled) return (
    <>
      <button type="button" onClick={() => setPromo(true)} className={cls} style={{ background: BG }} data-testid="dashboard-ea-card"><Decor /><Body enabled={false} /></button>
      <EaPromoDialog open={promo} onOpenChange={setPromo} />
    </>
  );
  return (
    <Link to={last ? eaVerifyUrl(last.company_id) : "/energy-analytics/overview"} className={cls} style={{ background: BG }} data-testid="dashboard-ea-card">
      <Decor /><Body isStaff={isStaff} last={last} enabled />
    </Link>
  );
}
